import React, { useRef, useState, useEffect } from "react";
import { Box, Button, Typography, Stack, Slider, Paper } from "@mui/material";

function formatTime(seconds) {
    if (seconds == null || isNaN(seconds)) return "--:--";
    const min = Math.floor(seconds / 60);
    const sec = (seconds % 60).toFixed(2);
    return `${min}:${sec.padStart(5, "0")}`;
}

function TimeRangeSelector({ videoFile, onSetTimeRange, onConfirm }) {
    const videoRef = useRef(null);
    const [videoSrc, setVideoSrc] = useState("");
    const [duration, setDuration] = useState(0);

    const [startTime, setStartTime] = useState(null);
    const [endTime, setEndTime] = useState(null);

    useEffect(() => {
        if (!videoFile) {
            setVideoSrc("");
            return;
        }
        const src = URL.createObjectURL(videoFile);
        setVideoSrc(src);

        return () => {
            URL.revokeObjectURL(src);
        };
    }, [videoFile]);

    const handleLoadedMetadata = (e) => {
        const d = e.target.duration;
        setDuration(d);

        // Default to the full duration of the video
        setStartTime(0);
        setEndTime(d);

        // Also update parent state immediately (without advancing the step)
        if (onSetTimeRange) {
            onSetTimeRange(0, d);
        }
    };

    const handleSetStart = () => {
        const t = videoRef.current.currentTime;
        setStartTime(t);
        // If end is before new start, clear it
        if (endTime != null && endTime <= t) {
            setEndTime(null);
        }
    };

    const handleConfirm = () => {
        if (startTime != null && endTime != null && endTime > startTime) {
            onSetTimeRange(startTime, endTime);
            if (onConfirm) {
                onConfirm();
            }
        }
    };

    const rangeValid = startTime != null && endTime != null && endTime > startTime;
    const sliderValue = rangeValid ? [startTime, endTime] : [0, 0];

    return (
        <Box className={"center-box"}>
            <Typography variant="h6" gutterBottom>
                Define Time Range
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Drag the two handles on the slider below to select the portion of the video you want to extract.
                The selected range will be sampled during processing.
            </Typography>

            <Box sx={{ mb: 2 }}>
                <video
                    ref={videoRef}
                    src={videoSrc}
                    controls
                    muted
                    onLoadedMetadata={handleLoadedMetadata}
                    style={{ maxWidth: "100%", maxHeight: "480px", background: "#000" }}
                />
            </Box>

            {/* Visual Range Slider */}
            <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: "background.default" }}>
                <Typography variant="body2" gutterBottom>
                    Selected Range
                </Typography>
                <Slider
                    value={rangeValid ? sliderValue : [0, duration || 0]}
                    min={0}
                    max={duration || 0}
                    step={0.01}
                    disabled={!duration}
                    valueLabelDisplay="auto"
                    valueLabelFormat={(v) => formatTime(v)}
                    sx={{
                        "& .MuiSlider-thumb": {
                            width: 16,
                            height: 16,
                        },
                    }}
                    onChange={(_, newValue) => {
                        // Allow direct manipulation of the range via the slider thumbs
                        if (Array.isArray(newValue)) {
                            const [newStart, newEnd] = newValue;
                            setStartTime(newStart);
                            setEndTime(newEnd);

                            // Seek video: prefer the end thumb if it moved more, otherwise start
                            if (videoRef.current) {
                                const prevStart = startTime ?? newStart;
                                const prevEnd = endTime ?? newEnd;
                                const startMoved = Math.abs(newStart - prevStart);
                                const endMoved = Math.abs(newEnd - prevEnd);

                                if (endMoved > startMoved) {
                                    videoRef.current.currentTime = newEnd;
                                } else {
                                    videoRef.current.currentTime = newStart;
                                }
                            }
                        }
                    }}
                />
                <Stack direction="row" justifyContent="space-between" sx={{ mt: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                        0s
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        {formatTime(duration)} ({duration.toFixed(1)}s)
                    </Typography>
                </Stack>
            </Paper>

            {/* Selection Summary */}
            <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: "background.paper" }}>
                <Stack spacing={0.5}>
                    <Typography>
                        Start: <strong>{formatTime(startTime)}</strong>
                    </Typography>
                    <Typography>
                        End: <strong>{formatTime(endTime)}</strong>
                    </Typography>
                    <Typography>
                        Duration:{" "}
                        <strong>
                            {rangeValid ? (endTime - startTime).toFixed(2) + "s" : "—"}
                        </strong>
                    </Typography>
                    {rangeValid && (
                        <Typography variant="body2" color="success.main">
                            Ready — {((endTime - startTime) * (1 / (1 / 30))).toFixed(0)} frames at 30 fps
                        </Typography>
                    )}
                </Stack>
            </Paper>

            <Button
                variant="contained"
                size="large"
                onClick={handleConfirm}
                disabled={!rangeValid}
            >
                Confirm Time Range &amp; Continue
            </Button>
        </Box>
    );
}

export default TimeRangeSelector;
